import React, { Suspense, lazy } from 'react';
import { Box, CircularProgress, CssBaseline, Skeleton } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProviderWrapper } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { BrandingProvider, useBranding } from './context/BrandingContext';
import { ModulesProvider } from './context/ModulesContext';
import Layout from './components/Layout';
import DirectionCacheProvider from './components/DirectionCacheProvider';
import PWAInstallBanner from './components/PWAInstallBanner';
import SeoPushChatbot from './components/SeoPushChatbot';
import ErrorBoundary from './components/ErrorBoundary';
import StuckOverlayWatchdog from './components/StuckOverlayWatchdog';

// Auth stays eager — first paint for login must be fast
import LoginPage from './pages/Login';

// Route-level code splitting — marketing + heavy app pages load on demand
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AdsLandingPage = lazy(() => import('./pages/AdsLandingPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const WhatsNewPage = lazy(() => import('./pages/WhatsNewPage'));
const PaymentResult = lazy(() => import('./pages/PaymentResult'));
const ProposalWizard = lazy(() => import('./pages/ProposalWizard'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const AccessibilityPage = lazy(() => import('./pages/legal/AccessibilityPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Organizations = lazy(() => import('./pages/Organizations'));
const Domains = lazy(() => import('./pages/Domains'));
const Mailboxes = lazy(() => import('./pages/Mailboxes'));
const WebmailInbox = lazy(() => import('./pages/Webmail/Inbox'));
const CalendarPage = lazy(() => import('./pages/Webmail/Calendar'));
const ReportsLogs = lazy(() => import('./pages/ReportsLogs'));
const UsersPage = lazy(() => import('./pages/Users'));
const Settings = lazy(() => import('./pages/Settings'));
const PlanManagement = lazy(() => import('./pages/PlanManagement'));
const Branding = lazy(() => import('./pages/Branding'));
const SystemHealth = lazy(() => import('./pages/SystemHealth'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const Billing = lazy(() => import('./pages/Billing'));
const Migration = lazy(() => import('./pages/Migration'));
const Automations = lazy(() => import('./pages/Automations'));
const Filing = lazy(() => import('./pages/Filing'));
const AutoReplyLogs = lazy(() => import('./pages/AutoReplyLogs'));
const ProposalAdmin = lazy(() => import('./pages/ProposalAdmin'));
const MarketingCockpit = lazy(() => import('./pages/MarketingCockpit'));
const SalesCRM = lazy(() => import('./pages/SalesCRM'));
const Integrations = lazy(() => import('./pages/Integrations'));
const SmartCleanup = lazy(() => import('./pages/SmartCleanup'));
const AiAgent = lazy(() => import('./pages/AiAgent'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress size={36} />
  </Box>
);

const AuthLoadingSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
    <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
    <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
    <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
  </Box>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingSkeleton />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children, roles = ['super_admin', 'org_admin', 'multi_org_admin'] }) => {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <Navigate to="/webmail" replace />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  const { branding, loading: brandingLoading } = useBranding();
  const isWhiteLabel = branding?.whitelabel;

  // Wait for branding to resolve before rendering routes (prevents flash of wrong content on white label domains)
  if (brandingLoading) {
    return <AuthLoadingSkeleton />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public pages — white label domains go straight to login */}
        <Route path="/" element={isWhiteLabel ? <Navigate to="/login" replace /> : <LandingPage />} />
        <Route path="/features" element={isWhiteLabel ? <Navigate to="/login" replace /> : <FeaturesPage />} />
        <Route path="/pricing" element={isWhiteLabel ? <Navigate to="/login" replace /> : <PricingPage />} />
        <Route path="/whats-new" element={isWhiteLabel ? <Navigate to="/login" replace /> : <WhatsNewPage />} />
        <Route path="/start" element={isWhiteLabel ? <Navigate to="/login" replace /> : <AdsLandingPage />} />
        <Route path="/lp/:slug" element={isWhiteLabel ? <Navigate to="/login" replace /> : <AdsLandingPage />} />
        <Route path="/payment/success" element={<PaymentResult />} />
        <Route path="/payment/failure" element={<PaymentResult />} />
        <Route path="/quote" element={isWhiteLabel ? <Navigate to="/login" replace /> : <ProposalWizard />} />
        <Route path="/proposal/:number" element={isWhiteLabel ? <Navigate to="/login" replace /> : <ProposalWizard />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/about" element={isWhiteLabel ? <Navigate to="/login" replace /> : <AboutPage />} />
        <Route path="/blog" element={isWhiteLabel ? <Navigate to="/login" replace /> : <BlogPage />} />
        <Route path="/blog/:slug" element={isWhiteLabel ? <Navigate to="/login" replace /> : <BlogPage />} />
        <Route path="/careers" element={isWhiteLabel ? <Navigate to="/login" replace /> : <CareersPage />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/help/:slug" element={<HelpCenterPage />} />
        <Route path="/api-docs" element={isWhiteLabel ? <Navigate to="/login" replace /> : <ApiDocsPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/compare" element={isWhiteLabel ? <Navigate to="/login" replace /> : <ComparePage />} />
        <Route path="/compare/:slug" element={isWhiteLabel ? <Navigate to="/login" replace /> : <ComparePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />

        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/app" element={<Navigate to={user?.role === 'user' ? '/webmail' : '/dashboard'} replace />} />

          <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
          <Route path="/organizations" element={<AdminRoute roles={['super_admin', 'multi_org_admin']}><Organizations /></AdminRoute>} />
          <Route path="/domains" element={<AdminRoute><Domains /></AdminRoute>} />
          <Route path="/mailboxes" element={<AdminRoute><Mailboxes /></AdminRoute>} />
          <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
          <Route path="/logs" element={<AdminRoute><ReportsLogs /></AdminRoute>} />
          <Route path="/plans" element={<AdminRoute><PlanManagement /></AdminRoute>} />
          <Route path="/branding" element={<AdminRoute><Branding /></AdminRoute>} />
          <Route path="/system-health" element={<AdminRoute roles={['super_admin']}><SystemHealth /></AdminRoute>} />
          <Route path="/audit-log" element={<AdminRoute><AuditLog /></AdminRoute>} />
          <Route path="/billing" element={<AdminRoute><Billing /></AdminRoute>} />
          <Route path="/migration" element={<AdminRoute><Migration /></AdminRoute>} />
          <Route path="/auto-reply-logs" element={<AdminRoute><AutoReplyLogs /></AdminRoute>} />
          <Route path="/proposals" element={<AdminRoute><ProposalAdmin /></AdminRoute>} />
          <Route path="/marketing" element={<AdminRoute roles={['super_admin']}><MarketingCockpit /></AdminRoute>} />
          <Route path="/sales" element={<AdminRoute roles={['super_admin']}><SalesCRM /></AdminRoute>} />
          <Route path="/integrations" element={<AdminRoute roles={['super_admin', 'org_admin', 'multi_org_admin']}><Integrations /></AdminRoute>} />

          <Route path="/automations" element={<Automations />} />
          <Route path="/filing" element={<Filing />} />
          <Route path="/ai-agent" element={<AiAgent />} />
          <Route path="/smart-cleanup" element={<SmartCleanup />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/webmail" element={<WebmailInbox />} />
          <Route path="/calendar" element={<CalendarPage />} />
        </Route>

        {/* Logged-in users hitting unknown URLs stay in-app */}
        <Route
          path="*"
          element={
            <Navigate
              to={user ? (user.role === 'user' ? '/webmail' : '/dashboard') : '/'}
              replace
            />
          }
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <LanguageProvider>
      <DirectionCacheProvider>
        <BrandingProvider>
          <ThemeProviderWrapper>
            <CssBaseline />
            <AuthProvider>
              <ModulesProvider>
                <Router>
                  <ErrorBoundary name="app-root">
                    <AppRoutes />
                    <PWAInstallBanner />
                    <SeoPushChatbot />
                    <StuckOverlayWatchdog />
                  </ErrorBoundary>
                </Router>
              </ModulesProvider>
            </AuthProvider>
          </ThemeProviderWrapper>
        </BrandingProvider>
      </DirectionCacheProvider>
    </LanguageProvider>
  );
}

export default App;
